// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// ================================================================

import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const TODAY = new Date().toISOString().split('T')[0];

const DOCUMENT_TYPES = [
  { key: 'nda', label: 'NDA (Mutual)', desc: 'Non-Disclosure Agreement — Protect confidential information between parties', icon: '\u{1F512}' },
  { key: 'service', label: 'Client Service Agreement', desc: 'SaaS subscription agreement — Define services, pricing, and terms', icon: '\u{1F91D}' },
  { key: 'commission', label: 'Commission Agreement', desc: 'Ambassador/sales commission terms — Define territories, rates, and obligations', icon: '\u{1F4B0}' },
  { key: 'loi', label: 'Letter of Intent', desc: 'Non-binding LOI for pilot programs and partnerships', icon: '\u{1F4C4}' },
  { key: 'ip', label: 'IP Assignment', desc: 'Intellectual property transfer agreement — Assign IP rights between parties', icon: '\u{1F4A1}' },
  { key: 'employment', label: 'Employment Agreement', desc: 'Standard employment terms — Define role, compensation, and obligations', icon: '\u{1F4BC}' },
];

const JURISDICTIONS = [
  'Wyoming', 'California', 'New York', 'Texas',
  'Alberta', 'British Columbia', 'Ontario', 'Saskatchewan', 'Manitoba', 'Quebec', 'Nova Scotia', 'New Brunswick'
];

const MT_BRANDS = [
  'CropPatrol', 'HaulSync', 'Directional Ops', 'LotSync', 'NurseVault', 'NurseVault CA',
  'Vantage Ops', 'BudLedger', 'OreSync', 'VacTraq', 'GlowDesk', 'ClosingDesk',
  'WrenchSync', 'TreadOps', 'HerdLedger', 'WellnessIQ', 'InsuranceBrokerIQ',
  'StageVault', 'MTIQ', 'FormGenius', 'IOD', 'IronLedger', 'Hoist Control', 'RigWarden', 'Innovation Hub'
];

const DEMO_DOCUMENTS = [
  { id: 1, type: 'NDA (Mutual)', counterparty: 'Acme Energy Corp', date: '2026-02-10', status: 'Final' },
  { id: 2, type: 'Client Service Agreement', counterparty: 'Summit Drilling Ltd.', date: '2026-02-08', status: 'Draft' },
  { id: 3, type: 'Commission Agreement', counterparty: 'Prairie Sales Group', date: '2026-02-05', status: 'Final' },
];

const INITIAL_COMMON = {
  providerName: 'Miskulin Technologies',
  providerAddress: '121 Rainbow Falls Bay, Chestermere, AB T1X 0S5, Canada',
  clientName: '',
  clientAddress: '',
  clientContactName: '',
  clientContactEmail: '',
  date: TODAY,
  governingLaw: 'Alberta',
};

const INITIAL_NDA = { confidentialityPeriod: '2 years', scopeOfConfidential: '' };
const INITIAL_SERVICE = { productName: '', platformUrl: '', monthlyFee: '', freeTrialPeriod: 'None', cancellationNotice: '30 days', slaUptime: '99.9%' };
const INITIAL_COMMISSION = { ambassadorName: '', territory: '', commissionRateY1: '', commissionRateRenewal: '', namedAccount: '', productVertical: MT_BRANDS[0] };
const INITIAL_LOI = { pilotDuration: '90', pilotScope: '', nonBinding: true };
const INITIAL_IP = {};
const INITIAL_EMPLOYMENT = {};

function generateNDAHtml(f) {
  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Mutual Non-Disclosure Agreement</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:0;color:#1a1a2e;background:#fff}
.header{background:linear-gradient(135deg,#1a1a2e 0%,#16213e 100%);color:#fff;padding:48px 60px 40px;text-align:center}
.header h1{margin:0 0 8px;font-size:28px;letter-spacing:1px}
.header p{margin:0;font-size:13px;color:#a0a0c0}
.content{max-width:800px;margin:0 auto;padding:40px 60px}
.parties{display:flex;gap:24px;margin:24px 0 32px}
.party-card{flex:1;background:#f8f9fa;border:1px solid #e0e0e0;border-radius:10px;padding:20px}
.party-card h3{margin:0 0 12px;font-size:14px;color:#6366f1;text-transform:uppercase;letter-spacing:1px}
.party-card p{margin:4px 0;font-size:13px;color:#333}
.section{margin-bottom:24px}
.section h2{font-size:16px;color:#1a1a2e;border-bottom:2px solid #6366f1;padding-bottom:6px;margin-bottom:12px}
.section p,.section li{font-size:13px;line-height:1.7;color:#333}
.sig-block{display:flex;gap:40px;margin-top:48px;padding-top:24px;border-top:2px solid #e0e0e0}
.sig-col{flex:1}
.sig-col .sig-line{border-bottom:1px solid #333;height:40px;margin-bottom:4px}
.sig-col p{font-size:12px;color:#666;margin:2px 0}
.footer{text-align:center;margin-top:48px;padding:20px;font-size:11px;color:#999;border-top:1px solid #eee}
</style></head><body>
<div class="header">
<h1>MUTUAL NON-DISCLOSURE AGREEMENT</h1>
<p>Confidential &mdash; ${f.providerName}</p>
</div>
<div class="content">
<p style="font-size:13px;color:#555;margin-bottom:24px">This Mutual Non-Disclosure Agreement (&ldquo;Agreement&rdquo;) is entered into as of <strong>${f.date}</strong> by and between the following parties:</p>
<div class="parties">
<div class="party-card">
<h3>Disclosing Party / Provider</h3>
<p><strong>${f.providerName}</strong></p>
<p>${f.providerAddress}</p>
</div>
<div class="party-card">
<h3>Receiving Party / Client</h3>
<p><strong>${f.clientName || '[Client Name]'}</strong></p>
<p>${f.clientAddress || '[Client Address]'}</p>
<p>Attn: ${f.clientContactName || '[Contact Name]'}</p>
<p>${f.clientContactEmail || '[Contact Email]'}</p>
</div>
</div>

<div class="section"><h2>1. Purpose</h2>
<p>The parties wish to explore a potential business relationship (the &ldquo;Purpose&rdquo;) and, in connection therewith, may disclose to each other certain Confidential Information. This Agreement sets forth the terms and conditions under which such information shall be disclosed and protected.</p></div>

<div class="section"><h2>2. Definition of Confidential Information</h2>
<p>As used herein, &ldquo;Confidential Information&rdquo; means any and all non-public, proprietary, or confidential information disclosed by either party to the other, whether orally, in writing, electronically, or by inspection of tangible objects, including but not limited to: trade secrets, business plans, financial data, customer lists, software code, algorithms, inventions, designs, strategies, pricing, and any other information designated as confidential or that reasonably should be understood to be confidential.</p>
${f.scopeOfConfidential ? `<p><strong>Additional Scope:</strong> ${f.scopeOfConfidential}</p>` : ''}</div>

<div class="section"><h2>3. Obligations of the Receiving Party</h2>
<p>The Receiving Party agrees to: (a) hold the Confidential Information in strict confidence; (b) not disclose the Confidential Information to any third party without prior written consent; (c) use the Confidential Information solely for the Purpose; (d) protect the Confidential Information with at least the same degree of care it uses for its own confidential information, but no less than reasonable care.</p></div>

<div class="section"><h2>4. Exclusions</h2>
<p>Confidential Information does not include information that: (a) is or becomes publicly available through no fault of the Receiving Party; (b) was already known to the Receiving Party prior to disclosure; (c) is independently developed by the Receiving Party without use of the Confidential Information; (d) is rightfully obtained from a third party without restriction.</p></div>

<div class="section"><h2>5. Term and Duration</h2>
<p>This Agreement shall remain in effect for a period of <strong>${f.confidentialityPeriod}</strong> from the date of execution, unless earlier terminated by either party upon thirty (30) days&rsquo; written notice. The obligations of confidentiality shall survive termination for the full duration specified herein.</p></div>

<div class="section"><h2>6. Return of Materials</h2>
<p>Upon termination of this Agreement or upon request by the Disclosing Party, the Receiving Party shall promptly return or destroy all Confidential Information and any copies thereof, and shall certify in writing that it has done so.</p></div>

<div class="section"><h2>7. No License Granted</h2>
<p>Nothing in this Agreement grants the Receiving Party any license, title, or ownership interest in or to any Confidential Information. All Confidential Information remains the sole property of the Disclosing Party.</p></div>

<div class="section"><h2>8. Remedies</h2>
<p>The parties acknowledge that any breach of this Agreement may cause irreparable harm for which monetary damages alone would be insufficient. In addition to any other remedies available at law or in equity, the non-breaching party shall be entitled to seek injunctive relief.</p></div>

<div class="section"><h2>9. Governing Law</h2>
<p>This Agreement shall be governed by and construed in accordance with the laws of the State/Province of <strong>${f.governingLaw}</strong>, without regard to conflicts of law principles.</p></div>

<div class="section"><h2>10. Entire Agreement</h2>
<p>This Agreement constitutes the entire agreement between the parties with respect to the subject matter hereof and supersedes all prior or contemporaneous understandings, representations, and agreements. This Agreement may be amended only by a written instrument signed by both parties.</p></div>

<div class="sig-block">
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.providerName}</strong></p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.clientName || '[Client Name]'}</strong></p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
</div>

<div class="footer">
<p>&copy; ${new Date().getFullYear()} ${f.providerName}. All rights reserved. This document is confidential and intended solely for the named parties.</p>
</div>
</div></body></html>`;
}

function generateServiceHtml(f) {
  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Client Service Agreement</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:0;color:#1a1a2e;background:#fff}
.header{background:linear-gradient(135deg,#1a1a2e 0%,#16213e 100%);color:#fff;padding:48px 60px 40px;text-align:center}
.header h1{margin:0 0 8px;font-size:28px;letter-spacing:1px}
.header p{margin:0;font-size:13px;color:#a0a0c0}
.content{max-width:800px;margin:0 auto;padding:40px 60px}
.parties{display:flex;gap:24px;margin:24px 0 32px}
.party-card{flex:1;background:#f8f9fa;border:1px solid #e0e0e0;border-radius:10px;padding:20px}
.party-card h3{margin:0 0 12px;font-size:14px;color:#6366f1;text-transform:uppercase;letter-spacing:1px}
.party-card p{margin:4px 0;font-size:13px;color:#333}
.section{margin-bottom:24px}
.section h2{font-size:16px;color:#1a1a2e;border-bottom:2px solid #6366f1;padding-bottom:6px;margin-bottom:12px}
.section p,.section li{font-size:13px;line-height:1.7;color:#333}
.highlight{background:#f0f0ff;border-left:4px solid #6366f1;padding:12px 16px;border-radius:0 8px 8px 0;margin:12px 0}
.sig-block{display:flex;gap:40px;margin-top:48px;padding-top:24px;border-top:2px solid #e0e0e0}
.sig-col{flex:1}
.sig-col .sig-line{border-bottom:1px solid #333;height:40px;margin-bottom:4px}
.sig-col p{font-size:12px;color:#666;margin:2px 0}
.footer{text-align:center;margin-top:48px;padding:20px;font-size:11px;color:#999;border-top:1px solid #eee}
</style></head><body>
<div class="header">
<h1>CLIENT SERVICE AGREEMENT</h1>
<p>SaaS Subscription Agreement &mdash; ${f.providerName}</p>
</div>
<div class="content">
<p style="font-size:13px;color:#555;margin-bottom:24px">This Client Service Agreement (&ldquo;Agreement&rdquo;) is entered into as of <strong>${f.date}</strong> by and between:</p>
<div class="parties">
<div class="party-card">
<h3>Service Provider</h3>
<p><strong>${f.providerName}</strong></p>
<p>${f.providerAddress}</p>
</div>
<div class="party-card">
<h3>Client</h3>
<p><strong>${f.clientName || '[Client Name]'}</strong></p>
<p>${f.clientAddress || '[Client Address]'}</p>
<p>Attn: ${f.clientContactName || '[Contact Name]'}</p>
<p>${f.clientContactEmail || '[Contact Email]'}</p>
</div>
</div>

<div class="section"><h2>1. Services</h2>
<p>Provider agrees to provide Client with access to <strong>${f.productName || '[Product/Service Name]'}</strong>, a cloud-based software-as-a-service platform accessible at <strong>${f.platformUrl || '[Platform URL]'}</strong> (the &ldquo;Service&rdquo;), subject to the terms and conditions of this Agreement.</p></div>

<div class="section"><h2>2. Subscription Fees</h2>
<div class="highlight">
<p><strong>Monthly Subscription Fee:</strong> $${f.monthlyFee || '0'} USD per month</p>
<p><strong>Free Trial Period:</strong> ${f.freeTrialPeriod}</p>
<p><strong>Billing Cycle:</strong> Monthly, in advance</p>
</div>
<p>Fees are non-refundable except as expressly provided herein. Provider reserves the right to adjust pricing with sixty (60) days&rsquo; written notice prior to the next renewal period.</p></div>

<div class="section"><h2>3. Term and Renewal</h2>
<p>This Agreement shall commence on the Effective Date and continue on a month-to-month basis unless terminated by either party with <strong>${f.cancellationNotice}</strong> written notice. If a free trial period is specified, the paid subscription begins upon expiration of the trial unless Client cancels prior to trial end.</p></div>

<div class="section"><h2>4. Service Level Agreement</h2>
<p>Provider guarantees an uptime of <strong>${f.slaUptime}</strong> measured on a monthly basis, excluding scheduled maintenance windows. In the event uptime falls below the guaranteed level, Client shall be entitled to a prorated service credit for the affected period.</p></div>

<div class="section"><h2>5. Data Ownership and Security</h2>
<p>Client retains full ownership of all data entered into the Service. Provider shall implement and maintain commercially reasonable administrative, technical, and physical safeguards to protect Client data against unauthorized access, loss, or alteration. Provider shall not sell, share, or otherwise disclose Client data to third parties except as required by law.</p></div>

<div class="section"><h2>6. Intellectual Property</h2>
<p>Provider retains all right, title, and interest in and to the Service, including all intellectual property rights therein. This Agreement does not convey to Client any ownership interest in the Service. Client is granted a limited, non-exclusive, non-transferable license to use the Service during the term of this Agreement.</p></div>

<div class="section"><h2>7. Confidentiality</h2>
<p>Each party agrees to maintain the confidentiality of the other party&rsquo;s proprietary information and to use such information solely for the purposes contemplated by this Agreement.</p></div>

<div class="section"><h2>8. Limitation of Liability</h2>
<p>In no event shall either party&rsquo;s aggregate liability exceed the total fees paid by Client in the twelve (12) months preceding the claim. Neither party shall be liable for indirect, incidental, special, consequential, or punitive damages.</p></div>

<div class="section"><h2>9. Termination</h2>
<p>Either party may terminate this Agreement: (a) with <strong>${f.cancellationNotice}</strong> written notice; (b) immediately upon material breach that remains uncured for fifteen (15) days after written notice; (c) immediately if the other party becomes insolvent or files for bankruptcy.</p></div>

<div class="section"><h2>10. Governing Law</h2>
<p>This Agreement shall be governed by the laws of the State/Province of <strong>${f.governingLaw}</strong>.</p></div>

<div class="sig-block">
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.providerName}</strong></p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.clientName || '[Client Name]'}</strong></p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
</div>

<div class="footer">
<p>&copy; ${new Date().getFullYear()} ${f.providerName}. All rights reserved.</p>
</div>
</div></body></html>`;
}

function generateCommissionHtml(f) {
  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Commission Agreement</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:0;color:#1a1a2e;background:#fff}
.header{background:linear-gradient(135deg,#1a1a2e 0%,#16213e 100%);color:#fff;padding:48px 60px 40px;text-align:center}
.header h1{margin:0 0 8px;font-size:28px;letter-spacing:1px}
.header p{margin:0;font-size:13px;color:#a0a0c0}
.content{max-width:800px;margin:0 auto;padding:40px 60px}
.parties{display:flex;gap:24px;margin:24px 0 32px}
.party-card{flex:1;background:#f8f9fa;border:1px solid #e0e0e0;border-radius:10px;padding:20px}
.party-card h3{margin:0 0 12px;font-size:14px;color:#6366f1;text-transform:uppercase;letter-spacing:1px}
.party-card p{margin:4px 0;font-size:13px;color:#333}
.section{margin-bottom:24px}
.section h2{font-size:16px;color:#1a1a2e;border-bottom:2px solid #6366f1;padding-bottom:6px;margin-bottom:12px}
.section p,.section li{font-size:13px;line-height:1.7;color:#333}
.highlight{background:#f0f0ff;border-left:4px solid #6366f1;padding:12px 16px;border-radius:0 8px 8px 0;margin:12px 0}
.sig-block{display:flex;gap:40px;margin-top:48px;padding-top:24px;border-top:2px solid #e0e0e0}
.sig-col{flex:1}
.sig-col .sig-line{border-bottom:1px solid #333;height:40px;margin-bottom:4px}
.sig-col p{font-size:12px;color:#666;margin:2px 0}
.footer{text-align:center;margin-top:48px;padding:20px;font-size:11px;color:#999;border-top:1px solid #eee}
</style></head><body>
<div class="header">
<h1>COMMISSION AGREEMENT</h1>
<p>Ambassador / Sales Commission Terms &mdash; ${f.providerName}</p>
</div>
<div class="content">
<p style="font-size:13px;color:#555;margin-bottom:24px">This Commission Agreement (&ldquo;Agreement&rdquo;) is entered into as of <strong>${f.date}</strong> by and between:</p>
<div class="parties">
<div class="party-card">
<h3>Company</h3>
<p><strong>${f.providerName}</strong></p>
<p>${f.providerAddress}</p>
</div>
<div class="party-card">
<h3>Ambassador / Sales Representative</h3>
<p><strong>${f.ambassadorName || '[Ambassador Name]'}</strong></p>
<p>${f.clientName ? `c/o ${f.clientName}` : '[Organization]'}</p>
<p>${f.clientAddress || '[Address]'}</p>
<p>${f.clientContactEmail || '[Email]'}</p>
</div>
</div>

<div class="section"><h2>1. Appointment</h2>
<p>Company hereby appoints the Ambassador as a non-exclusive sales representative for the <strong>${f.productVertical || '[Product Vertical]'}</strong> platform within the designated territory, subject to the terms herein.</p></div>

<div class="section"><h2>2. Territory</h2>
<p>The Ambassador&rsquo;s designated territory is: <strong>${f.territory || '[Territory]'}</strong>. Ambassador shall focus sales efforts within this territory but may refer leads from outside the territory subject to Company approval.</p></div>

<div class="section"><h2>3. Commission Structure</h2>
<div class="highlight">
<p><strong>Year 1 Commission Rate:</strong> ${f.commissionRateY1 || '0'}% of net subscription revenue</p>
<p><strong>Renewal Commission Rate:</strong> ${f.commissionRateRenewal || '0'}% of net renewal revenue</p>
<p><strong>Product Vertical:</strong> ${f.productVertical || '[Product Vertical]'}</p>
${f.namedAccount ? `<p><strong>Named Account:</strong> ${f.namedAccount}</p>` : ''}
</div>
<p>Commissions are calculated on net revenue collected (excluding taxes, refunds, and chargebacks) and paid within thirty (30) days of collection. Commission is earned upon Company&rsquo;s receipt of payment from the referred client.</p></div>

<div class="section"><h2>4. Obligations of Ambassador</h2>
<p>Ambassador agrees to: (a) represent Company products professionally and accurately; (b) not make unauthorized commitments or representations on behalf of Company; (c) comply with all applicable laws and regulations; (d) maintain confidentiality of Company proprietary information; (e) provide monthly activity reports detailing sales pipeline and client interactions.</p></div>

<div class="section"><h2>5. Obligations of Company</h2>
<p>Company agrees to: (a) provide Ambassador with current marketing materials, demos, and training; (b) process and fulfill all sales referrals in a timely manner; (c) pay commissions per the schedule above; (d) notify Ambassador of material changes to products or pricing with reasonable advance notice.</p></div>

<div class="section"><h2>6. Non-Compete and Non-Solicitation</h2>
<p>During the term of this Agreement and for twelve (12) months thereafter, Ambassador shall not directly promote or sell competing SaaS products within the designated territory or solicit Company clients introduced through this relationship.</p></div>

<div class="section"><h2>7. Term and Termination</h2>
<p>This Agreement is effective as of the date first written above and continues for an initial term of twelve (12) months, renewing automatically for successive twelve-month periods. Either party may terminate with sixty (60) days&rsquo; written notice. Accrued but unpaid commissions shall survive termination.</p></div>

<div class="section"><h2>8. Governing Law</h2>
<p>This Agreement shall be governed by the laws of the State/Province of <strong>${f.governingLaw}</strong>.</p></div>

<div class="sig-block">
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.providerName}</strong></p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.ambassadorName || '[Ambassador Name]'}</strong></p>
<p>Ambassador / Sales Representative</p>
<p>Date: _______________</p>
</div>
</div>

<div class="footer">
<p>&copy; ${new Date().getFullYear()} ${f.providerName}. All rights reserved.</p>
</div>
</div></body></html>`;
}

function generateLOIHtml(f) {
  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Letter of Intent</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:0;color:#1a1a2e;background:#fff}
.header{background:linear-gradient(135deg,#1a1a2e 0%,#16213e 100%);color:#fff;padding:48px 60px 40px;text-align:center}
.header h1{margin:0 0 8px;font-size:28px;letter-spacing:1px}
.header p{margin:0;font-size:13px;color:#a0a0c0}
.content{max-width:800px;margin:0 auto;padding:40px 60px}
.parties{display:flex;gap:24px;margin:24px 0 32px}
.party-card{flex:1;background:#f8f9fa;border:1px solid #e0e0e0;border-radius:10px;padding:20px}
.party-card h3{margin:0 0 12px;font-size:14px;color:#6366f1;text-transform:uppercase;letter-spacing:1px}
.party-card p{margin:4px 0;font-size:13px;color:#333}
.section{margin-bottom:24px}
.section h2{font-size:16px;color:#1a1a2e;border-bottom:2px solid #6366f1;padding-bottom:6px;margin-bottom:12px}
.section p{font-size:13px;line-height:1.7;color:#333}
.notice{background:#fff8e1;border:1px solid #ffe082;border-radius:8px;padding:16px;margin:20px 0;font-size:13px;color:#795548}
.highlight{background:#f0f0ff;border-left:4px solid #6366f1;padding:12px 16px;border-radius:0 8px 8px 0;margin:12px 0}
.sig-block{display:flex;gap:40px;margin-top:48px;padding-top:24px;border-top:2px solid #e0e0e0}
.sig-col{flex:1}
.sig-col .sig-line{border-bottom:1px solid #333;height:40px;margin-bottom:4px}
.sig-col p{font-size:12px;color:#666;margin:2px 0}
.footer{text-align:center;margin-top:48px;padding:20px;font-size:11px;color:#999;border-top:1px solid #eee}
</style></head><body>
<div class="header">
<h1>LETTER OF INTENT</h1>
<p>${f.nonBinding ? 'Non-Binding' : 'Binding'} &mdash; Pilot Program / Partnership</p>
</div>
<div class="content">
${f.nonBinding ? '<div class="notice"><strong>NON-BINDING NOTICE:</strong> This Letter of Intent is non-binding and does not create any legal obligation on either party. It is intended solely to outline the proposed terms for further negotiation and does not constitute a contract or agreement.</div>' : ''}
<p style="font-size:13px;color:#555;margin-bottom:24px">This Letter of Intent (&ldquo;LOI&rdquo;) is issued as of <strong>${f.date}</strong> to outline the proposed terms for a pilot program between:</p>
<div class="parties">
<div class="party-card">
<h3>Provider</h3>
<p><strong>${f.providerName}</strong></p>
<p>${f.providerAddress}</p>
</div>
<div class="party-card">
<h3>Prospective Client</h3>
<p><strong>${f.clientName || '[Client Name]'}</strong></p>
<p>${f.clientAddress || '[Client Address]'}</p>
<p>Attn: ${f.clientContactName || '[Contact Name]'}</p>
<p>${f.clientContactEmail || '[Contact Email]'}</p>
</div>
</div>

<div class="section"><h2>1. Purpose</h2>
<p>This LOI outlines the intent of both parties to enter into a pilot program to evaluate the feasibility of a longer-term business relationship. The pilot is designed to demonstrate value and allow both parties to assess compatibility.</p></div>

<div class="section"><h2>2. Pilot Program Details</h2>
<div class="highlight">
<p><strong>Pilot Duration:</strong> ${f.pilotDuration} days from commencement</p>
</div>
${f.pilotScope ? `<p><strong>Scope of Pilot:</strong> ${f.pilotScope}</p>` : '<p><strong>Scope:</strong> To be defined in a subsequent service agreement.</p>'}</div>

<div class="section"><h2>3. Proposed Terms</h2>
<p>During the pilot period, Provider shall make available to the Prospective Client a demonstration version or full access to the platform as mutually agreed. Specific commercial terms, including pricing and service levels, will be negotiated and memorialized in a definitive service agreement following successful completion of the pilot.</p></div>

<div class="section"><h2>4. Confidentiality</h2>
<p>Both parties agree that any information exchanged during the pilot evaluation shall be treated as confidential. Neither party shall disclose the other&rsquo;s proprietary information to third parties without prior written consent.</p></div>

<div class="section"><h2>5. Next Steps</h2>
<p>Upon execution of this LOI, the parties intend to: (a) schedule a kickoff meeting within five (5) business days; (b) define pilot success metrics; (c) negotiate and execute a definitive service agreement prior to the expiration of the pilot period.</p></div>

<div class="section"><h2>6. ${f.nonBinding ? 'Non-Binding Nature' : 'Binding Provisions'}</h2>
${f.nonBinding
  ? '<p>This LOI is intended as an expression of interest only and does not create any binding obligation on either party. Either party may withdraw from negotiations at any time without liability. Only a definitive agreement signed by both parties shall create binding obligations.</p>'
  : '<p>The parties intend for this LOI to create binding obligations with respect to the terms outlined herein. A definitive agreement incorporating these terms shall be executed within thirty (30) days.</p>'
}</div>

<div class="section"><h2>7. Governing Law</h2>
<p>This LOI shall be interpreted under the laws of the State/Province of <strong>${f.governingLaw}</strong>.</p></div>

<div class="sig-block">
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.providerName}</strong></p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.clientName || '[Client Name]'}</strong></p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
</div>

<div class="footer">
<p>&copy; ${new Date().getFullYear()} ${f.providerName}. All rights reserved.</p>
</div>
</div></body></html>`;
}

function generateIPAssignmentHtml(f) {
  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>IP Assignment Agreement</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:0;color:#1a1a2e;background:#fff}
.header{background:linear-gradient(135deg,#1a1a2e 0%,#16213e 100%);color:#fff;padding:48px 60px 40px;text-align:center}
.header h1{margin:0 0 8px;font-size:28px;letter-spacing:1px}
.header p{margin:0;font-size:13px;color:#a0a0c0}
.content{max-width:800px;margin:0 auto;padding:40px 60px}
.parties{display:flex;gap:24px;margin:24px 0 32px}
.party-card{flex:1;background:#f8f9fa;border:1px solid #e0e0e0;border-radius:10px;padding:20px}
.party-card h3{margin:0 0 12px;font-size:14px;color:#6366f1;text-transform:uppercase;letter-spacing:1px}
.party-card p{margin:4px 0;font-size:13px;color:#333}
.section{margin-bottom:24px}
.section h2{font-size:16px;color:#1a1a2e;border-bottom:2px solid #6366f1;padding-bottom:6px;margin-bottom:12px}
.section p,.section li{font-size:13px;line-height:1.7;color:#333}
.sig-block{display:flex;gap:40px;margin-top:48px;padding-top:24px;border-top:2px solid #e0e0e0}
.sig-col{flex:1}
.sig-col .sig-line{border-bottom:1px solid #333;height:40px;margin-bottom:4px}
.sig-col p{font-size:12px;color:#666;margin:2px 0}
.footer{text-align:center;margin-top:48px;padding:20px;font-size:11px;color:#999;border-top:1px solid #eee}
</style></head><body>
<div class="header">
<h1>INTELLECTUAL PROPERTY ASSIGNMENT AGREEMENT</h1>
<p>IP Transfer &mdash; ${f.providerName}</p>
</div>
<div class="content">
<p style="font-size:13px;color:#555;margin-bottom:24px">This Intellectual Property Assignment Agreement (&ldquo;Agreement&rdquo;) is entered into as of <strong>${f.date}</strong> by and between:</p>
<div class="parties">
<div class="party-card">
<h3>Assignee (Receiving Party)</h3>
<p><strong>${f.providerName}</strong></p>
<p>${f.providerAddress}</p>
</div>
<div class="party-card">
<h3>Assignor (Transferring Party)</h3>
<p><strong>${f.clientName || '[Assignor Name]'}</strong></p>
<p>${f.clientAddress || '[Assignor Address]'}</p>
<p>Attn: ${f.clientContactName || '[Contact Name]'}</p>
<p>${f.clientContactEmail || '[Contact Email]'}</p>
</div>
</div>

<div class="section"><h2>1. Assignment of Intellectual Property</h2>
<p>Assignor hereby irrevocably assigns, transfers, and conveys to Assignee all right, title, and interest in and to all Intellectual Property (as defined below) created, conceived, developed, or reduced to practice by Assignor during the course of engagement with or on behalf of Assignee, including all patents, copyrights, trademarks, trade secrets, and any other intellectual property rights therein.</p></div>

<div class="section"><h2>2. Definition of Intellectual Property</h2>
<p>&ldquo;Intellectual Property&rdquo; means all inventions (whether patentable or not), discoveries, improvements, original works of authorship, software code, algorithms, designs, processes, formulas, trade secrets, know-how, data compilations, documentation, and any other intellectual creation, in any form or medium, that is created, conceived, or developed by Assignor in connection with services provided to Assignee.</p></div>

<div class="section"><h2>3. Work Made for Hire</h2>
<p>To the extent that any Intellectual Property qualifies as a &ldquo;work made for hire&rdquo; under applicable copyright law, it shall be deemed a work made for hire and Assignee shall be the author thereof. To the extent any such work does not qualify as a work made for hire, this Agreement serves as an irrevocable assignment of all rights therein to Assignee.</p></div>

<div class="section"><h2>4. Moral Rights Waiver</h2>
<p>To the fullest extent permitted by applicable law, Assignor hereby irrevocably waives all moral rights, rights of attribution, rights of integrity, and any similar rights in the Intellectual Property. Assignor agrees not to assert any such rights against Assignee or its successors and assigns.</p></div>

<div class="section"><h2>5. Further Assurances</h2>
<p>Assignor agrees to execute all documents and take all actions reasonably requested by Assignee to perfect, register, or enforce the rights assigned hereunder, including patent applications, copyright registrations, and any other filings. Assignor hereby irrevocably appoints Assignee as attorney-in-fact to execute such documents if Assignor fails to do so within fifteen (15) days of request.</p></div>

<div class="section"><h2>6. Representations and Warranties</h2>
<p>Assignor represents and warrants that: (a) Assignor is the sole creator and owner of the Intellectual Property; (b) the Intellectual Property is original and does not infringe any third-party rights; (c) Assignor has not previously assigned, licensed, or encumbered the Intellectual Property; (d) there are no pending or threatened claims relating to the Intellectual Property.</p></div>

<div class="section"><h2>7. Consideration</h2>
<p>In consideration for this assignment, Assignee agrees to provide Assignor with the compensation set forth in the applicable employment, contractor, or service agreement between the parties. Assignor acknowledges that such compensation is adequate and sufficient consideration for this assignment.</p></div>

<div class="section"><h2>8. Governing Law</h2>
<p>This Agreement shall be governed by the laws of the State/Province of <strong>${f.governingLaw}</strong>.</p></div>

<div class="sig-block">
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.providerName}</strong> (Assignee)</p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.clientName || '[Assignor Name]'}</strong> (Assignor)</p>
<p>Signature</p>
<p>Date: _______________</p>
</div>
</div>

<div class="footer">
<p>&copy; ${new Date().getFullYear()} ${f.providerName}. All rights reserved.</p>
</div>
</div></body></html>`;
}

function generateEmploymentHtml(f) {
  return `<!DOCTYPE html>
<html><head><meta charset="UTF-8"><title>Employment Agreement</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;margin:0;padding:0;color:#1a1a2e;background:#fff}
.header{background:linear-gradient(135deg,#1a1a2e 0%,#16213e 100%);color:#fff;padding:48px 60px 40px;text-align:center}
.header h1{margin:0 0 8px;font-size:28px;letter-spacing:1px}
.header p{margin:0;font-size:13px;color:#a0a0c0}
.content{max-width:800px;margin:0 auto;padding:40px 60px}
.parties{display:flex;gap:24px;margin:24px 0 32px}
.party-card{flex:1;background:#f8f9fa;border:1px solid #e0e0e0;border-radius:10px;padding:20px}
.party-card h3{margin:0 0 12px;font-size:14px;color:#6366f1;text-transform:uppercase;letter-spacing:1px}
.party-card p{margin:4px 0;font-size:13px;color:#333}
.section{margin-bottom:24px}
.section h2{font-size:16px;color:#1a1a2e;border-bottom:2px solid #6366f1;padding-bottom:6px;margin-bottom:12px}
.section p,.section li{font-size:13px;line-height:1.7;color:#333}
.sig-block{display:flex;gap:40px;margin-top:48px;padding-top:24px;border-top:2px solid #e0e0e0}
.sig-col{flex:1}
.sig-col .sig-line{border-bottom:1px solid #333;height:40px;margin-bottom:4px}
.sig-col p{font-size:12px;color:#666;margin:2px 0}
.footer{text-align:center;margin-top:48px;padding:20px;font-size:11px;color:#999;border-top:1px solid #eee}
</style></head><body>
<div class="header">
<h1>EMPLOYMENT AGREEMENT</h1>
<p>Standard Employment Terms &mdash; ${f.providerName}</p>
</div>
<div class="content">
<p style="font-size:13px;color:#555;margin-bottom:24px">This Employment Agreement (&ldquo;Agreement&rdquo;) is entered into as of <strong>${f.date}</strong> by and between:</p>
<div class="parties">
<div class="party-card">
<h3>Employer</h3>
<p><strong>${f.providerName}</strong></p>
<p>${f.providerAddress}</p>
</div>
<div class="party-card">
<h3>Employee</h3>
<p><strong>${f.clientName || '[Employee Name]'}</strong></p>
<p>${f.clientAddress || '[Employee Address]'}</p>
<p>Email: ${f.clientContactEmail || '[Employee Email]'}</p>
</div>
</div>

<div class="section"><h2>1. Position and Duties</h2>
<p>Employee is hired for the position of <strong>${f.clientContactName || '[Job Title]'}</strong>. Employee shall perform such duties and responsibilities as are customarily associated with such position, as well as such other duties as may be assigned from time to time by the Employer. Employee shall devote full business time, attention, and best efforts to the performance of duties hereunder.</p></div>

<div class="section"><h2>2. Compensation</h2>
<p>Employee shall receive compensation as determined by Employer and communicated in a separate compensation schedule or offer letter. Compensation may include base salary, performance bonuses, and such other benefits as Employer may offer from time to time at its sole discretion. Compensation shall be paid in accordance with Employer&rsquo;s standard payroll practices.</p></div>

<div class="section"><h2>3. Employment Term</h2>
<p>This Agreement establishes an at-will employment relationship. Either party may terminate this Agreement at any time, with or without cause, upon fourteen (14) days&rsquo; written notice. Nothing in this Agreement guarantees employment for any specific period.</p></div>

<div class="section"><h2>4. Confidentiality</h2>
<p>Employee agrees to maintain the strict confidentiality of all proprietary information, trade secrets, client data, business strategies, financial information, software code, algorithms, and other confidential materials belonging to Employer. This obligation shall survive the termination of employment and continue in perpetuity.</p></div>

<div class="section"><h2>5. Intellectual Property</h2>
<p>All work product, inventions, discoveries, designs, software, and other intellectual property created by Employee during the course of employment, or using Employer&rsquo;s resources, shall be the sole and exclusive property of Employer. Employee hereby assigns all rights therein to Employer and agrees to execute any documents necessary to perfect such assignment.</p></div>

<div class="section"><h2>6. Non-Compete and Non-Solicitation</h2>
<p>During employment and for twelve (12) months following termination, Employee shall not: (a) directly or indirectly engage in any business that competes with Employer within the same industry verticals; (b) solicit, recruit, or hire any employee or contractor of Employer; (c) solicit or divert any client or customer of Employer.</p></div>

<div class="section"><h2>7. Return of Property</h2>
<p>Upon termination of employment, Employee shall immediately return all property belonging to Employer, including but not limited to: equipment, documents, files, software, access credentials, and any copies of Confidential Information in any form.</p></div>

<div class="section"><h2>8. Remote Work and Equipment</h2>
<p>Employee may be permitted to work remotely at Employer&rsquo;s discretion. Employer shall provide necessary equipment and tools for the performance of duties. Employee is responsible for maintaining a secure and professional work environment.</p></div>

<div class="section"><h2>9. Code of Conduct</h2>
<p>Employee agrees to comply with all Employer policies, procedures, and codes of conduct as may be established from time to time. Employee shall conduct business ethically and professionally at all times.</p></div>

<div class="section"><h2>10. Governing Law</h2>
<p>This Agreement shall be governed by the laws of the State/Province of <strong>${f.governingLaw}</strong>.</p></div>

<div class="section"><h2>11. Entire Agreement</h2>
<p>This Agreement, together with any offer letter and compensation schedule, constitutes the entire agreement between the parties. It supersedes all prior negotiations, representations, and agreements. This Agreement may be amended only by a written instrument signed by both parties.</p></div>

<div class="sig-block">
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.providerName}</strong></p>
<p>Authorized Representative</p>
<p>Date: _______________</p>
</div>
<div class="sig-col">
<div class="sig-line"></div>
<p><strong>${f.clientName || '[Employee Name]'}</strong></p>
<p>Employee Signature</p>
<p>Date: _______________</p>
</div>
</div>

<div class="footer">
<p>&copy; ${new Date().getFullYear()} ${f.providerName}. All rights reserved.</p>
</div>
</div></body></html>`;
}

function generateDocumentHtml(type, formData) {
  switch (type) {
    case 'nda': return generateNDAHtml(formData);
    case 'service': return generateServiceHtml(formData);
    case 'commission': return generateCommissionHtml(formData);
    case 'loi': return generateLOIHtml(formData);
    case 'ip': return generateIPAssignmentHtml(formData);
    case 'employment': return generateEmploymentHtml(formData);
    default: return '<p>Select a document type to generate.</p>';
  }
}

export default function LegalDocGeneratorPage() {
  const { user } = useAuth();

  const [selectedType, setSelectedType] = useState(null);
  const [formData, setFormData] = useState({ ...INITIAL_COMMON });
  const [generatedHtml, setGeneratedHtml] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [savedDocs, setSavedDocs] = useState(DEMO_DOCUMENTS);
  const [saveNotice, setSaveNotice] = useState('');

  const updateField = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const handleSelectType = (typeKey) => {
    setSelectedType(typeKey);
    setGeneratedHtml('');
    const extras = typeKey === 'nda' ? INITIAL_NDA
      : typeKey === 'service' ? INITIAL_SERVICE
      : typeKey === 'commission' ? INITIAL_COMMISSION
      : typeKey === 'loi' ? INITIAL_LOI
      : typeKey === 'ip' ? INITIAL_IP
      : typeKey === 'employment' ? INITIAL_EMPLOYMENT
      : {};
    setFormData({ ...INITIAL_COMMON, ...extras });
  };

  const handleGenerate = () => {
    setIsGenerating(true);
    setGeneratedHtml('');
    setTimeout(() => {
      const html = generateDocumentHtml(selectedType, formData);
      setGeneratedHtml(html);
      setIsGenerating(false);
    }, 1800);
  };

  const handleDownloadHtml = () => {
    if (!generatedHtml) return;
    const typeLabel = DOCUMENT_TYPES.find(t => t.key === selectedType)?.label || 'Document';
    const filename = `${typeLabel.replace(/[^a-zA-Z0-9]/g, '-')}_${formData.clientName ? formData.clientName.replace(/[^a-zA-Z0-9]/g, '-') : 'Draft'}_${formData.date}.html`;
    const blob = new Blob([generatedHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!generatedHtml) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(generatedHtml);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 500);
  };

  const handleSaveToAccount = () => {
    if (!generatedHtml) return;
    const typeLabel = DOCUMENT_TYPES.find(t => t.key === selectedType)?.label || 'Document';
    const newDoc = {
      id: savedDocs.length + 1,
      type: typeLabel,
      counterparty: formData.clientName || formData.ambassadorName || 'Draft',
      date: formData.date,
      status: 'Draft',
    };
    setSavedDocs(prev => [newDoc, ...prev]);
    setSaveNotice('Document saved to your account.');
    setTimeout(() => setSaveNotice(''), 3000);
  };

  const handleReset = () => {
    setSelectedType(null);
    setFormData({ ...INITIAL_COMMON });
    setGeneratedHtml('');
    setIsGenerating(false);
  };

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    background: 'var(--bg)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    color: 'var(--text)',
    fontSize: 13,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const selectStyle = {
    ...inputStyle,
    cursor: 'pointer',
    appearance: 'auto',
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: 80,
    resize: 'vertical',
    fontFamily: 'inherit',
  };

  const labelStyle = {
    display: 'block',
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--text-muted)',
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  };

  const fieldGroup = { marginBottom: 16 };

  const btnPrimary = {
    padding: '12px 24px',
    background: '#6366f1',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 8,
  };

  const btnSecondary = {
    padding: '10px 20px',
    background: 'var(--bg)',
    color: 'var(--text)',
    border: '1px solid var(--border)',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };

  const renderCommonFields = () => (
    <>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={fieldGroup}>
          <label style={labelStyle}>Provider / Company Name</label>
          <input style={inputStyle} value={formData.providerName} onChange={e => updateField('providerName', e.target.value)} />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>Provider Address</label>
          <input style={inputStyle} value={formData.providerAddress} onChange={e => updateField('providerAddress', e.target.value)} />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={fieldGroup}>
          <label style={labelStyle}>Client / Counterparty Name</label>
          <input style={inputStyle} value={formData.clientName} onChange={e => updateField('clientName', e.target.value)} placeholder="e.g. Acme Energy Corp" />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>Client Address</label>
          <input style={inputStyle} value={formData.clientAddress} onChange={e => updateField('clientAddress', e.target.value)} placeholder="Street, City, State/Province, ZIP" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={fieldGroup}>
          <label style={labelStyle}>Client Contact Name</label>
          <input style={inputStyle} value={formData.clientContactName} onChange={e => updateField('clientContactName', e.target.value)} placeholder="Full name" />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>Client Contact Email</label>
          <input style={inputStyle} type="email" value={formData.clientContactEmail} onChange={e => updateField('clientContactEmail', e.target.value)} placeholder="email@company.com" />
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div style={fieldGroup}>
          <label style={labelStyle}>Date</label>
          <input style={inputStyle} type="date" value={formData.date} onChange={e => updateField('date', e.target.value)} />
        </div>
        <div style={fieldGroup}>
          <label style={labelStyle}>Governing Law (State / Province)</label>
          <select style={selectStyle} value={formData.governingLaw} onChange={e => updateField('governingLaw', e.target.value)}>
            {JURISDICTIONS.map(j => <option key={j} value={j}>{j}</option>)}
          </select>
        </div>
      </div>
    </>
  );

  const renderTypeSpecificFields = () => {
    if (!selectedType) return null;

    switch (selectedType) {
      case 'nda':
        return (
          <>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
              <h3 style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, margin: '0 0 14px' }}>NDA-Specific Fields</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={fieldGroup}>
                <label style={labelStyle}>Confidentiality Period</label>
                <select style={selectStyle} value={formData.confidentialityPeriod} onChange={e => updateField('confidentialityPeriod', e.target.value)}>
                  <option value="1 year">1 Year</option>
                  <option value="2 years">2 Years</option>
                  <option value="5 years">5 Years</option>
                </select>
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Scope of Confidential Information (Optional)</label>
              <textarea style={textareaStyle} value={formData.scopeOfConfidential} onChange={e => updateField('scopeOfConfidential', e.target.value)} placeholder="Describe specific categories of confidential information, if any..." />
            </div>
          </>
        );

      case 'service':
        return (
          <>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
              <h3 style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, margin: '0 0 14px' }}>Service Agreement Fields</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={fieldGroup}>
                <label style={labelStyle}>Product / Service Name</label>
                <input style={inputStyle} value={formData.productName} onChange={e => updateField('productName', e.target.value)} placeholder="e.g. Directional Ops Platform" />
              </div>
              <div style={fieldGroup}>
                <label style={labelStyle}>Platform URL</label>
                <input style={inputStyle} value={formData.platformUrl} onChange={e => updateField('platformUrl', e.target.value)} placeholder="e.g. https://directionaliq.pages.dev" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div style={fieldGroup}>
                <label style={labelStyle}>Monthly Fee (USD)</label>
                <div style={{ position: 'relative' }}>
                  <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14, fontWeight: 600 }}>$</span>
                  <input style={{ ...inputStyle, paddingLeft: 28 }} value={formData.monthlyFee} onChange={e => updateField('monthlyFee', e.target.value)} placeholder="0.00" />
                </div>
              </div>
              <div style={fieldGroup}>
                <label style={labelStyle}>Free Trial Period</label>
                <select style={selectStyle} value={formData.freeTrialPeriod} onChange={e => updateField('freeTrialPeriod', e.target.value)}>
                  <option value="None">None</option>
                  <option value="30 days">30 Days</option>
                  <option value="60 days">60 Days</option>
                  <option value="90 days">90 Days</option>
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={labelStyle}>SLA Uptime Guarantee</label>
                <select style={selectStyle} value={formData.slaUptime} onChange={e => updateField('slaUptime', e.target.value)}>
                  <option value="99%">99%</option>
                  <option value="99.5%">99.5%</option>
                  <option value="99.9%">99.9%</option>
                </select>
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Cancellation Notice Period</label>
              <select style={selectStyle} value={formData.cancellationNotice} onChange={e => updateField('cancellationNotice', e.target.value)}>
                <option value="30 days">30 Days</option>
                <option value="60 days">60 Days</option>
                <option value="90 days">90 Days</option>
              </select>
            </div>
          </>
        );

      case 'commission':
        return (
          <>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
              <h3 style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, margin: '0 0 14px' }}>Commission Agreement Fields</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={fieldGroup}>
                <label style={labelStyle}>Ambassador Name</label>
                <input style={inputStyle} value={formData.ambassadorName} onChange={e => updateField('ambassadorName', e.target.value)} placeholder="Full name of sales representative" />
              </div>
              <div style={fieldGroup}>
                <label style={labelStyle}>Territory</label>
                <input style={inputStyle} value={formData.territory} onChange={e => updateField('territory', e.target.value)} placeholder="e.g. Western Canada, Permian Basin" />
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
              <div style={fieldGroup}>
                <label style={labelStyle}>Commission Rate Year 1 (%)</label>
                <div style={{ position: 'relative' }}>
                  <input style={{ ...inputStyle, paddingRight: 28 }} value={formData.commissionRateY1} onChange={e => updateField('commissionRateY1', e.target.value)} placeholder="e.g. 15" />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14, fontWeight: 600 }}>%</span>
                </div>
              </div>
              <div style={fieldGroup}>
                <label style={labelStyle}>Commission Rate Renewal (%)</label>
                <div style={{ position: 'relative' }}>
                  <input style={{ ...inputStyle, paddingRight: 28 }} value={formData.commissionRateRenewal} onChange={e => updateField('commissionRateRenewal', e.target.value)} placeholder="e.g. 10" />
                  <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', fontSize: 14, fontWeight: 600 }}>%</span>
                </div>
              </div>
              <div style={fieldGroup}>
                <label style={labelStyle}>Product Vertical</label>
                <select style={selectStyle} value={formData.productVertical} onChange={e => updateField('productVertical', e.target.value)}>
                  {MT_BRANDS.map(b => <option key={b} value={b}>{b}</option>)}
                </select>
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Named Account (Optional)</label>
              <input style={inputStyle} value={formData.namedAccount} onChange={e => updateField('namedAccount', e.target.value)} placeholder="Specific account this ambassador owns, if any" />
            </div>
          </>
        );

      case 'loi':
        return (
          <>
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
              <h3 style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, margin: '0 0 14px' }}>Letter of Intent Fields</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div style={fieldGroup}>
                <label style={labelStyle}>Pilot Duration</label>
                <select style={selectStyle} value={formData.pilotDuration} onChange={e => updateField('pilotDuration', e.target.value)}>
                  <option value="30">30 Days</option>
                  <option value="60">60 Days</option>
                  <option value="90">90 Days</option>
                  <option value="180">180 Days</option>
                </select>
              </div>
              <div style={fieldGroup}>
                <label style={{ ...labelStyle, marginBottom: 10 }}>Non-Binding</label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: 'var(--text)' }}>
                  <input
                    type="checkbox"
                    checked={formData.nonBinding}
                    onChange={e => updateField('nonBinding', e.target.checked)}
                    style={{ width: 18, height: 18, accentColor: '#6366f1', cursor: 'pointer' }}
                  />
                  This LOI is non-binding
                </label>
                <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                  A non-binding LOI outlines proposed terms without creating legal obligations. Recommended for pilot programs.
                </div>
              </div>
            </div>
            <div style={fieldGroup}>
              <label style={labelStyle}>Pilot Scope / Description</label>
              <textarea style={textareaStyle} value={formData.pilotScope} onChange={e => updateField('pilotScope', e.target.value)} placeholder="Describe the objectives, deliverables, and success criteria for the pilot program..." />
            </div>
          </>
        );

      case 'ip':
        return (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
            <h3 style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, margin: '0 0 14px' }}>IP Assignment Fields</h3>
            <div style={{
              padding: 14,
              background: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--text-muted)',
              lineHeight: 1.6,
            }}>
              The IP Assignment template uses the common fields above. The Assignor (transferring party) information is entered in the Client fields. The Assignee (receiving party) defaults to Miskulin Technologies. All work product, inventions, and intellectual property are assigned to the Assignee under this agreement.
            </div>
          </div>
        );

      case 'employment':
        return (
          <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, marginTop: 8 }}>
            <h3 style={{ color: 'var(--text)', fontSize: 14, fontWeight: 600, margin: '0 0 14px' }}>Employment Agreement Fields</h3>
            <div style={{
              padding: 14,
              background: 'rgba(99,102,241,0.08)',
              border: '1px solid rgba(99,102,241,0.2)',
              borderRadius: 8,
              fontSize: 13,
              color: 'var(--text-muted)',
              lineHeight: 1.6,
            }}>
              Enter the Employee&rsquo;s name in the &ldquo;Client / Counterparty Name&rdquo; field, their address in &ldquo;Client Address&rdquo;, their Job Title in &ldquo;Client Contact Name&rdquo;, and their email in &ldquo;Client Contact Email&rdquo;. The employer defaults to Miskulin Technologies. Compensation details should be attached separately via an offer letter.
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="page-body" style={{ padding: 24 }}>
      {/* Header */}
      <div className="page-header" style={{ marginBottom: 28 }}>
        <h1 className="page-title" style={{ color: 'var(--text)', fontSize: 26, fontWeight: 700, margin: '0 0 6px' }}>
          Legal Document Generator
        </h1>
        <p className="page-subtitle" style={{ color: 'var(--text-muted)', fontSize: 14, margin: 0 }}>
          Generate professional legal documents in minutes. Powered by AI, formatted for print.
        </p>
      </div>

      {/* Document Type Selector */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: '0 0 14px' }}>
          Select Document Type
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 14,
        }}>
          {DOCUMENT_TYPES.map(dt => (
            <button
              key={dt.key}
              onClick={() => handleSelectType(dt.key)}
              style={{
                padding: 20,
                background: selectedType === dt.key ? 'rgba(99,102,241,0.08)' : 'var(--card)',
                border: selectedType === dt.key ? '2px solid #6366f1' : '1px solid var(--border)',
                borderRadius: 12,
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 0.15s ease',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                <span style={{ fontSize: 22 }}>{dt.icon}</span>
                <span style={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: selectedType === dt.key ? '#6366f1' : 'var(--text)',
                }}>{dt.label}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.5 }}>
                {dt.desc}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Form + Preview Two-Column Layout */}
      {selectedType && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: window.innerWidth > 900 ? '3fr 2fr' : '1fr',
          gap: 20,
          marginBottom: 28,
        }}>
          {/* Left Column: Form */}
          <div style={{
            padding: 24,
            background: 'var(--card)',
            borderRadius: 12,
            border: '1px solid var(--border)',
          }}>
            <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: '0 0 20px' }}>
              Document Details
            </h2>

            {renderCommonFields()}
            {renderTypeSpecificFields()}

            <div style={{ marginTop: 20, display: 'flex', gap: 12 }}>
              <button
                onClick={handleGenerate}
                disabled={isGenerating}
                style={{
                  ...btnPrimary,
                  opacity: isGenerating ? 0.7 : 1,
                  cursor: isGenerating ? 'wait' : 'pointer',
                }}
              >
                {isGenerating ? 'Generating...' : 'Generate Document'}
              </button>
              <button onClick={handleReset} style={btnSecondary}>
                Reset
              </button>
            </div>
          </div>

          {/* Right Column: Preview */}
          <div style={{
            padding: 24,
            background: 'var(--card)',
            borderRadius: 12,
            border: '1px solid var(--border)',
            display: 'flex',
            flexDirection: 'column',
          }}>
            <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>
              Document Preview
            </h2>

            {isGenerating && (
              <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16,
                padding: 40,
              }}>
                <div style={{
                  width: 48,
                  height: 48,
                  border: '4px solid var(--border)',
                  borderTopColor: '#6366f1',
                  borderRadius: '50%',
                  animation: 'ldg-spin 1s linear infinite',
                }} />
                <style>{`@keyframes ldg-spin{to{transform:rotate(360deg)}}`}</style>
                <div style={{ color: 'var(--text-muted)', fontSize: 14, fontWeight: 500 }}>
                  AI is generating your document...
                </div>
                <div style={{ width: '80%', height: 4, background: 'var(--border)', borderRadius: 2, overflow: 'hidden' }}>
                  <div style={{
                    width: '60%',
                    height: '100%',
                    background: '#6366f1',
                    borderRadius: 2,
                    animation: 'ldg-prog 1.8s ease-in-out',
                  }} />
                  <style>{`@keyframes ldg-prog{from{width:0}to{width:100%}}`}</style>
                </div>
              </div>
            )}

            {!isGenerating && !generatedHtml && (
              <div style={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 40,
                color: 'var(--text-muted)',
                fontSize: 14,
                textAlign: 'center',
                lineHeight: 1.6,
              }}>
                Fill out the form and click &ldquo;Generate Document&rdquo; to preview your legal document here.
              </div>
            )}

            {!isGenerating && generatedHtml && (
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div style={{
                  flex: 1,
                  background: '#fff',
                  borderRadius: 8,
                  border: '1px solid var(--border)',
                  overflow: 'auto',
                  minHeight: 400,
                  maxHeight: 600,
                }}>
                  <iframe
                    title="Document Preview"
                    srcDoc={generatedHtml}
                    style={{
                      width: '100%',
                      height: '100%',
                      minHeight: 400,
                      border: 'none',
                      borderRadius: 8,
                    }}
                  />
                </div>

                {/* Action Buttons */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
                  <button onClick={handleDownloadHtml} style={btnPrimary}>
                    Download HTML
                  </button>
                  <button onClick={handlePrint} style={btnSecondary}>
                    Print / Save PDF
                  </button>
                  <button onClick={handleSaveToAccount} style={btnSecondary}>
                    Save to Account
                  </button>
                  <button onClick={handleReset} style={btnSecondary}>
                    Generate Another
                  </button>
                </div>

                {saveNotice && (
                  <div style={{
                    padding: '10px 14px',
                    background: 'rgba(16,185,129,0.1)',
                    border: '1px solid rgba(16,185,129,0.3)',
                    borderRadius: 8,
                    color: '#10b981',
                    fontSize: 13,
                    fontWeight: 500,
                  }}>
                    {saveNotice}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* My Documents Section */}
      <div style={{
        padding: 24,
        background: 'var(--card)',
        borderRadius: 12,
        border: '1px solid var(--border)',
      }}>
        <h2 style={{ color: 'var(--text)', fontSize: 16, fontWeight: 600, margin: '0 0 16px' }}>
          My Documents
        </h2>

        <div style={{ overflow: 'hidden', borderRadius: 8, border: '1px solid var(--border)' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg)' }}>
                {['Document Type', 'Counterparty', 'Date Generated', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '10px 14px',
                    textAlign: 'left',
                    fontSize: 11,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    color: 'var(--text-muted)',
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {savedDocs.map(doc => (
                <tr key={doc.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <td style={{ padding: '10px 14px', color: 'var(--text)', fontSize: 13, fontWeight: 500 }}>
                    {doc.type}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text)', fontSize: 13 }}>
                    {doc.counterparty}
                  </td>
                  <td style={{ padding: '10px 14px', color: 'var(--text-muted)', fontSize: 12 }}>
                    {doc.date}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{
                      padding: '3px 10px',
                      borderRadius: 10,
                      fontSize: 11,
                      fontWeight: 600,
                      background: doc.status === 'Final' ? 'rgba(16,185,129,0.15)' : 'rgba(245,158,11,0.15)',
                      color: doc.status === 'Final' ? '#10b981' : '#f59e0b',
                    }}>{doc.status}</span>
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <button style={{
                      padding: '5px 12px',
                      background: 'var(--bg)',
                      border: '1px solid var(--border)',
                      borderRadius: 6,
                      color: '#6366f1',
                      fontSize: 12,
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}>
                      Download
                    </button>
                  </td>
                </tr>
              ))}
              {savedDocs.length === 0 && (
                <tr>
                  <td colSpan={5} style={{
                    padding: 24,
                    textAlign: 'center',
                    color: 'var(--text-muted)',
                    fontSize: 13,
                  }}>
                    No documents generated yet. Select a document type above to get started.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
